import { Locales } from '@griply/types';
import { formatTimeslotLabel } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeslotContextMenu } from 'shared/components/connected/timeslot-context-menu/timeslot-context-menu';
import { TaskMetaInteraction } from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { Task } from 'shared/types/task';
import { Timeslot } from 'shared/types/timeslot';
import { Timestamp } from 'shared/types/timestamp';

import * as Styled from './task-meta-interaction-due-date.style';

export type TaskMetaInteractionTimeslotProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  task: Task;
  timeFormat: TimeFormatOptions;
  representedDay: Timestamp;
  value?: Timeslot | null;
  hasDueDate?: boolean;
};

export const TaskMetaInteractionTimeslot: React.FC<
  TaskMetaInteractionTimeslotProps
> = ({
  task,
  timeFormat,
  representedDay,
  value,
  hasDueDate,

  ...rest
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const label = useMemo(
    () =>
      formatTimeslotLabel(value, {
        timeFormat,
        isNL: locale === Locales.NlNL,
      }),
    [locale, timeFormat, value],
  );

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        onClick={openMenu}
        label={label}
        selected={menuOpen}
        hasTooltip
        tooltipLabel={t('task.schedule.skip.tooltip.label')}
        minimalStartSpacing={hasDueDate}
        {...rest}
      />

      {menuOpen && (
        <TimeslotContextMenu
          referenceElement={containerRef}
          task={task}
          representedDay={representedDay}
          onClose={closeMenu}
          position="bottom-start"
        />
      )}
    </Styled.Container>
  );
};
